/**
 * «Cambió el plano» (OPUS ci, idea 4): el IMPACTO de una revisión nueva en la hoja. Parte PURA ($0, sin IA):
 *
 *  - `comoSeMidioDeLaFoto`: con qué ajustes se midió un renglón (magnitud, unidad, alto, espesor, altos de vanos).
 *  - `crearMapeador` / `mapearSeleccion`: lleva la selección de un renglón de la revisión A a la B.
 *  - `impactoEnLaHoja`: renglón por renglón, qué pasa con su cantidad y su importe.
 *  - `compararRevisiones`: todo junto (emparejar, cambios, impacto, lo nuevo sin concepto, totales).
 *
 * La regla magnitud → cantidad NO vive aquí: medir un elemento (`medirA`/`medirB`) y sacar sus magnitudes
 * (`magnitudesA`/`magnitudesB`) los inyecta quien llama (en la app, la misma medición de la pantalla del plano).
 *
 * Nada se decide solo cuando hay duda: una pareja dudosa, un renglón corregido a mano o una hoja medida con otros
 * ajustes quedan «revisar» y la cantidad nueva, si la hay, va como propuesta.
 *
 * Por diseño:
 *  - `ComoSeMidio` es UNO por renglón (sale de su foto). Un renglón que junta elementos medidos con altos distintos
 *    no se puede reproducir con un solo alto: la autocomprobación falla y sale «revisar». Es lo esperado.
 *  - Una foto vieja sin `inputExtra` se toma como 'ninguno' (lo que pedían los conceptos antes de guardarlo).
 *  - Los importes que se calculan aquí son VISTA PREVIA. Al aplicar (T2) el importe válido es el que devuelve el
 *    servidor tras `fijarCantidad` (redondeo de numeric en Postgres vs. el de JS: puede diferir ±0.01).
 */
import type { PlanoMagnitudConcepto, PlanoSeleccion } from './domain.js';
import {
  claveDeElemento,
  elementosDeSeleccion,
  fotoDeRegla,
  juntarTodas,
  seleccionDeElementos,
  toleranciaDeCuadre,
  type DerivacionDeComplementario,
  type ElementoDelGenerador,
  type FotoGenerador,
  type LineaDeRegla,
  type LineaGenerador,
} from './numerosGeneradores.js';
import { derivarCantidadComplementaria, porMedidaDelConcepto, type MagnitudesPrincipal } from './cantidadComplementaria.js';
import { claveDeTipoDeVano } from './muroConVanos.js';
import {
  cambiosDePiezas,
  emparejarPiezas,
  emparejarVistas,
  type CambioDePieza,
  type ParametrosCambioDePlano,
  type ParejaDePiezas,
  type PiezaComparable,
  type ResultadoEmparejado,
  type ResultadoVistas,
  type VistaComparable,
} from './cambioDePlano.js';

type UnElemento = Omit<PlanoSeleccion, 'ademas'>;

// ---------------------------------------------------------------------------------------------------
// Cómo se midió
// ---------------------------------------------------------------------------------------------------

/** Con qué ajustes se midió UN renglón (uno solo por renglón: ver la cabecera). */
export interface ComoSeMidio {
  magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null;
  unidad: string | null;
  altoStr: string;
  espesorStr: string;
  /** Alto de cada tipo de vano (`claveDeTipoDeVano`), como lo escribió quien cotiza. */
  altosVanos: Record<string, string>;
}

/** El concepto que lleva HOY el renglón (manda sobre lo que quedó en la foto). */
export interface ConceptoVigente {
  magnitud?: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null;
  unidad?: string | null;
}

/** El tipo de vano de una línea de descuento: el guardado o, en fotos de antes, el que dice su texto. */
function vanoDeLinea(l: LineaGenerador): string | null {
  if (l.vano) return l.vano;
  if (l.largo == null) return null;
  const m = /«(.+)»/.exec(l.localizacion);
  return claveDeTipoDeVano(m ? m[1]! : null, l.largo);
}

/**
 * Fotos guardadas ANTES de que se guardara `inputExtra`: qué pidió el concepto además de la medida, deducido de lo que la
 * propia foto trae. Un área o un volumen cuyas líneas del plano traen ALTO (largo × alto, no una pieza medida del modelo)
 * se midió con el alto de muro (y con el espesor si trae `espesorUsadoM`); si no, es la medida directa. La autocomprobación
 * de A atrapa cualquier deducción equivocada: nunca se aplica algo medido con ajustes que no reproducen lo guardado.
 */
function inputExtraDeUnaFotoVieja(f: FotoGenerador): NonNullable<FotoGenerador['inputExtra']> {
  if (f.magnitud !== 'area' && f.magnitud !== 'volumen') return 'ninguno';
  const conAlto = !!f.altoUsado?.valorM || f.lineas.some((l) => l.clase === 'medida' && !l.pieza && l.alto != null);
  if (!conAlto) return 'ninguno';
  return f.magnitud === 'volumen' || f.espesorUsadoM ? 'alto_y_espesor' : 'alto';
}

/**
 * Con qué ajustes se midió un renglón, sacado de su foto. null = no se puede saber (foto de antes, sin foto, o
 * que no sale de piezas medidas). La magnitud y la unidad del concepto VIGENTE mandan: la foto puede arrastrar
 * los ajustes del concepto anterior al juntar renglones. Sin `inputExtra` en la foto (fotos viejas) → 'ninguno'.
 */
export function comoSeMidioDeLaFoto(f: FotoGenerador | null | undefined, vigente?: ConceptoVigente): ComoSeMidio | null {
  if (!f || f.version !== 2 || f.origen !== 'plano') return null;
  const deLaFoto: ComoSeMidio['magnitud'] = f.magnitud ? { magnitud: f.magnitud, inputExtra: f.inputExtra ?? inputExtraDeUnaFotoVieja(f) } : null;
  const magnitud = vigente?.magnitud !== undefined && vigente.magnitud !== null ? vigente.magnitud : deLaFoto;
  // El alto general: el guardado; si no (muro con vanos), el único alto de sus tramos dibujados.
  let altoStr = '';
  if (f.altoUsado?.valorM) altoStr = String(f.altoUsado.valorM);
  else {
    const altos = new Set(f.lineas.filter((l) => l.clase === 'medida' && !l.pieza && l.alto != null).map((l) => l.alto!));
    if (altos.size === 1) altoStr = String([...altos][0]);
  }
  const altosVanos: Record<string, string> = {};
  for (const l of f.lineas) {
    if (l.clase !== 'descuento' || l.alto == null || l.pieza) continue;
    const k = vanoDeLinea(l);
    if (k) altosVanos[k] = String(l.alto);
  }
  return {
    magnitud,
    unidad: vigente?.unidad ?? f.unidad ?? null,
    altoStr,
    espesorStr: f.espesorUsadoM ? String(f.espesorUsadoM) : '',
    altosVanos,
  };
}

// ---------------------------------------------------------------------------------------------------
// Llevar la selección de A a B
// ---------------------------------------------------------------------------------------------------

export interface ConteoDePiezas {
  agregadas: number;
  quitadas: number;
  cambiaron: number;
  /** Piezas de la selección que en B son de OTRO tipo (siguen existiendo, no se quitaron). */
  cambioDeTipo: number;
  dudosas: number;
}

export interface SeleccionMapeada {
  /** La selección en B; null = ya no hay ninguna de sus piezas (o el tipo desapareció). */
  seleccion: UnElemento | null;
  /** La selección de A cubría el tipo (o la capa) entero. */
  cubreTodo: boolean;
  /**
   * La selección era PARCIAL y no queda ninguna de sus piezas. No se puede escribir como selección (sin `ids` sería el
   * tipo entero): el renglón no reescribe su selección.
   */
  parcialVacia: boolean;
  /** No es seguro que sean las mismas piezas: nunca cambia sola. */
  dudoso: boolean;
  /** Sin duda en las piezas, pero el renglón queda para revisar (piezas nuevas o de otro tipo, parcial vacía…). */
  revisar: boolean;
  motivo?: string;
  piezas: ConteoDePiezas;
}

export interface ContextoMapeo {
  formato: 'ifc' | 'dwg';
  piezasA: readonly PiezaComparable[];
  piezasB: readonly PiezaComparable[];
  emparejado: ResultadoEmparejado;
  cambios?: readonly CambioDePieza[];
  /** DWG: las vistas emparejadas (`emparejarVistas`). Sin esto, una selección con vista queda «revisar». */
  vistas?: ResultadoVistas;
}

const CERO: ConteoDePiezas = { agregadas: 0, quitadas: 0, cambiaron: 0, cambioDeTipo: 0, dudosas: 0 };
const YA_NO_ESTA = 'Ya no está en la revisión nueva.';
const NO_ES_SEGURO = 'No es seguro que sean las mismas piezas: revísalo.';
const plural = (n: number, s: string, p: string) => (n === 1 ? s : p);
const unir = (...xs: (string | undefined | null)[]) => xs.filter(Boolean).join(' ') || undefined;

/**
 * Prepara los índices una vez y devuelve la función que lleva UNA selección de A a B:
 *  - cubría el tipo entero → todas las piezas de B de ese tipo (las nuevas entran);
 *  - parcial (`ids`) → las piezas emparejadas (las nuevas NO entran: el renglón queda «revisar»);
 *  - tipo que ya no existe en B → se sigue por la mayoría de sus piezas emparejadas, pero DUDOSO (pudo fusionarse
 *    con otro tipo: dos renglones tomarían las mismas piezas);
 *  - un tipo de B que recibe piezas de A de FUERA de la selección (fusión) → dudoso;
 *  - una pareja dudosa nunca cambia sola una selección parcial;
 *  - pieza de la selección que pasó a otro tipo → «revisar» (no se quitó; ver `nuevosSinConcepto`);
 *  - DWG: la vista (planta) se lleva por `emparejarVistas` (su id es posicional); sin pareja segura → dudoso.
 * DWG: la clase de la pieza («bloque»/«capa») debe ser el tipo de la selección (una capa y un bloque pueden
 * llamarse igual).
 */
export function crearMapeador(ctx: ContextoMapeo): (sel: UnElemento) => SeleccionMapeada {
  const clave = (tipo: string, grupo: string) => (ctx.formato === 'dwg' ? `${tipo}\u0000${grupo}` : grupo);
  const claveDePieza = (p: PiezaComparable) => clave(p.clase, p.grupo);
  const agrupar = (ps: readonly PiezaComparable[]) => {
    const m = new Map<string, PiezaComparable[]>();
    for (const p of ps) (m.get(claveDePieza(p)) ?? m.set(claveDePieza(p), []).get(claveDePieza(p))!).push(p);
    return m;
  };
  const grupoA = agrupar(ctx.piezasA);
  const grupoB = agrupar(ctx.piezasB);
  const porIdB = new Map(ctx.piezasB.map((p) => [p.id, p] as const));
  const parejaDeA = new Map<string, ParejaDePiezas>(ctx.emparejado.parejas.map((x) => [x.a, x] as const));
  const parejaDeB = new Map<string, ParejaDePiezas>(ctx.emparejado.parejas.map((x) => [x.b, x] as const));
  const ambiguasA = new Set(ctx.emparejado.ambiguas.map((x) => x.a));
  const soloB = new Set(ctx.emparejado.soloB);
  const cambiaronA = new Set(
    (ctx.cambios ?? []).filter((c) => (c.tipo === 'cambio_medida' || c.tipo === 'movida') && c.a).map((c) => c.a!.id),
  );
  const vistaDeA = new Map((ctx.vistas?.parejas ?? []).map((x) => [x.a, x] as const));

  return (sel) => {
    const nada = (o: Partial<SeleccionMapeada> & { motivo: string }): SeleccionMapeada => ({
      seleccion: null,
      cubreTodo: !sel.ids,
      parcialVacia: !!sel.ids,
      dudoso: true,
      revisar: false,
      piezas: { ...CERO },
      ...o,
    });

    // DWG: la planta de la selección en B (el id 'v2' de A puede ser otra planta en B).
    let vistaB = sel.vista;
    let dudosoVista = false;
    if (sel.vista && ctx.formato === 'dwg') {
      const pv = vistaDeA.get(sel.vista);
      if (!pv) return nada({ motivo: 'No encontré esa planta en la revisión nueva: revísalo.' });
      vistaB = pv.b;
      dudosoVista = pv.dudoso;
    }

    const k = clave(sel.tipo, sel.clave);
    // DWG: una selección con planta solo habla de esa planta (cada capa es una pieza por planta). Sin esto, una capa
    // que desaparece de SU planta pero sigue en otras se mediría completa en B.
    const deLaVista = (ps: readonly PiezaComparable[], v: string | undefined) => (ctx.formato === 'dwg' && v ? ps.filter((p) => p.nivel === v) : ps);
    const enA = deLaVista(grupoA.get(k) ?? [], sel.vista);
    // `ids` presente (aunque vacío) = selección PARCIAL: nunca se lee como «el tipo entero».
    const ids = sel.ids ? new Set(sel.ids) : null;
    const selA = ids ? enA.filter((p) => ids.has(p.id)) : enA;
    if (ids && selA.length === 0) {
      return nada({ motivo: 'Las piezas de este renglón no se encuentran en el plano anterior: revísalo.' });
    }
    const cubreTodo = !ids || enA.every((p) => ids.has(p.id));
    const selAIds = new Set(selA.map((p) => p.id));
    const ambiguasDeSel = selA.filter((p) => ambiguasA.has(p.id)).length;

    // ¿A qué tipo de B va? El mismo nombre si existe; si no, el de la mayoría de sus piezas emparejadas (dudoso).
    let destino: string | null = sel.clave;
    let dudoso = dudosoVista;
    const motivos: (string | undefined)[] = [];
    const hayEnB = deLaVista(grupoB.get(k) ?? [], vistaB).length > 0;
    if (!hayEnB) {
      destino = null;
      const votos = new Map<string, number>();
      let emparejadas = 0;
      for (const p of selA) {
        const par = parejaDeA.get(p.id);
        const pb = par ? porIdB.get(par.b) : undefined;
        if (!par || !pb || (ctx.formato === 'dwg' && pb.clase !== sel.tipo)) continue;
        emparejadas++;
        votos.set(pb.grupo, (votos.get(pb.grupo) ?? 0) + 1);
      }
      let mejor: [string, number] | null = null;
      for (const v of votos) if (!mejor || v[1] > mejor[1]) mejor = v;
      if (mejor && mejor[1] * 2 > emparejadas) {
        destino = mejor[0];
        dudoso = true;
        motivos.push(`Su tipo ahora se llama «${destino}».`);
      }
    }
    if (destino == null) {
      const dud = ambiguasDeSel > 0 || dudosoVista;
      return {
        seleccion: null,
        cubreTodo,
        parcialVacia: !cubreTodo,
        dudoso: dud,
        revisar: false,
        motivo: dud ? 'No es seguro qué piezas de la revisión nueva son las de este renglón.' : YA_NO_ESTA,
        piezas: { ...CERO, quitadas: selA.length, dudosas: ambiguasDeSel },
      };
    }

    const enB = deLaVista(grupoB.get(clave(sel.tipo, destino)) ?? [], vistaB);
    const enBIds = new Set(enB.map((p) => p.id));
    // Las piezas de la selección que siguen en ese tipo de B, con su pareja; y las que pasaron a otro tipo.
    const seQuedan = new Map<string, ParejaDePiezas>();
    let aOtroTipo = 0;
    for (const p of selA) {
      const par = parejaDeA.get(p.id);
      if (!par) continue;
      if (enBIds.has(par.b)) seQuedan.set(par.b, par);
      else if (porIdB.has(par.b)) aOtroTipo++;
    }
    const parejasDudosas = [...seQuedan.values()].filter((x) => x.dudoso).length;
    const piezas: ConteoDePiezas = {
      agregadas: 0,
      quitadas: selA.length - seQuedan.size - aOtroTipo,
      cambiaron: [...seQuedan.values()].filter((x) => cambiaronA.has(x.a)).length,
      cambioDeTipo: aOtroTipo,
      dudosas: parejasDudosas + ambiguasDeSel,
    };
    let resultado: string[];
    let revisar = false;
    if (cubreTodo) {
      resultado = enB.map((p) => p.id);
      piezas.agregadas = enB.filter((p) => !seQuedan.has(p.id)).length;
      // Fusión: piezas de este tipo en B que vienen de piezas de A de FUERA de la selección (de otro tipo).
      const deFuera = enB.filter((p) => {
        const par = parejaDeB.get(p.id);
        return par != null && !selAIds.has(par.a);
      }).length;
      if (deFuera > 0) {
        dudoso = true;
        motivos.push(`${deFuera} ${plural(deFuera, 'pieza de este tipo venía', 'piezas de este tipo venían')} de otro tipo: puede que otro renglón ya las cobre.`);
      }
    } else {
      resultado = [...seQuedan.keys()];
      dudoso = dudoso || parejasDudosas > 0 || ambiguasDeSel > 0;
      const nuevasFuera = enB.filter((p) => soloB.has(p.id)).length;
      if (nuevasFuera > 0) {
        revisar = true;
        motivos.push(`Hay ${nuevasFuera} ${plural(nuevasFuera, 'pieza nueva', 'piezas nuevas')} de este tipo que el renglón no incluía: decide si las agregas.`);
      }
      if (aOtroTipo > 0) {
        revisar = true;
        motivos.push(`${aOtroTipo} ${plural(aOtroTipo, 'pieza de este renglón ahora es', 'piezas de este renglón ahora son')} de otro tipo: decide dónde van.`);
      }
    }
    if (dudoso) motivos.push(NO_ES_SEGURO);
    if (resultado.length === 0) {
      // Parcial que se quedó sin piezas mientras su tipo sigue en el plano: no se reescribe ni se decide solo.
      if (!cubreTodo && enB.length > 0) {
        revisar = true;
        motivos.push('Las piezas de este renglón ya no están, pero su tipo sigue en el plano: decide si lo quitas.');
      }
      return {
        seleccion: null,
        cubreTodo,
        parcialVacia: !cubreTodo,
        dudoso,
        revisar,
        motivo: dudoso || revisar ? unir(...motivos) : YA_NO_ESTA,
        piezas,
      };
    }
    const seleccion: UnElemento = {
      tipo: sel.tipo,
      clave: destino,
      ...(sel.ids ? { ids: resultado } : {}),
      ...(vistaB ? { vista: vistaB } : {}),
    };
    const motivo = unir(...motivos);
    return { seleccion, cubreTodo, parcialVacia: false, dudoso, revisar, ...(motivo ? { motivo } : {}), piezas };
  };
}

/** Lleva UNA selección de A a B (para muchas, usar `crearMapeador` una vez). */
export function mapearSeleccion(sel: UnElemento, ctx: ContextoMapeo): SeleccionMapeada {
  return crearMapeador(ctx)(sel);
}

// ---------------------------------------------------------------------------------------------------
// El impacto en la hoja
// ---------------------------------------------------------------------------------------------------

export type EstadoImpacto = 'igual' | 'cambia' | 'revisar' | 'ya_no_esta' | 'no_se_recalcula';

/** Lo que hace falta de un renglón de la hoja (subconjunto de `PlanoRenglon` + su análisis). */
export interface RenglonParaImpacto {
  id: string;
  /** Complementario: los principales de los que sale. */
  principalId?: string | null;
  padres?: string[];
  unidad: string | null;
  cantidad: number;
  cantidadMedida?: number | null;
  /** `quote_concepts.precio_unitario`. */
  precioUnitario: number | null;
  /** `quote_concepts.importe` (de catálogo). */
  importe: number | null;
  /** `quote_concepts.apu_ia_costo_directo` (análisis con IA), si lo tiene. */
  apuIaCostoDirecto?: number | null;
  /** `quote_concepts.apu_ia_importe`, si lo tiene. */
  apuIaImporte?: number | null;
  seleccion: PlanoSeleccion | null;
  generador?: FotoGenerador | null;
  /** La magnitud del concepto que lleva HOY el renglón (manda sobre la de la foto). */
  magnitudConcepto?: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null;
}

export interface ImpactoDeRenglon {
  id: string;
  estado: EstadoImpacto;
  /** Por qué, en palabras para quien cotiza. */
  motivo: string | null;
  /** Quien cotiza corrigió la cantidad a mano: la nueva es solo una propuesta. */
  aMano: boolean;
  /** La cantidad nueva se aplica sin preguntar ('cambia' o 'ya_no_esta', y no corregida a mano). */
  cantidadSeAplicaSola: boolean;
  /** La selección nueva se aplica sin preguntar (es identidad, no cantidad): sin duda en las piezas. */
  seleccionSeAplicaSola: boolean;
  cantidadAntes: number;
  /** La cantidad que da la revisión nueva (en 'revisar', propuesta). null = no hay propuesta. */
  cantidadNueva: number | null;
  delta: number | null;
  /** Columna `importe` (catálogo), antes y con la cantidad nueva (vista previa: ver la cabecera). */
  importeAntes: number | null;
  importeNuevo: number | null;
  /** Columna `apu_ia_importe` (análisis con IA), antes y con la cantidad nueva. */
  apuIaImporteAntes: number | null;
  apuIaImporteNuevo: number | null;
  /** null = la selección no cambia. */
  seleccionNueva: PlanoSeleccion | null;
  /** La foto de la medición en la revisión nueva (cuadra con `cantidadNueva`). */
  generadorNuevo: FotoGenerador | null;
  piezas: ConteoDePiezas;
}

export interface EntradaImpacto {
  renglones: readonly RenglonParaImpacto[];
  mapear: (sel: UnElemento) => SeleccionMapeada;
  /** Mide UN elemento en la revisión A / B con esos ajustes (la medición de la pantalla del plano). */
  medirA: (sel: UnElemento, como: ComoSeMidio) => FotoGenerador | null;
  medirB: (sel: UnElemento, como: ComoSeMidio) => FotoGenerador | null;
  /** Las magnitudes de UN elemento (para los complementarios). A sirve para la autocomprobación. */
  magnitudesA: (sel: UnElemento, como: ComoSeMidio) => MagnitudesPrincipal | null;
  magnitudesB: (sel: UnElemento, como: ComoSeMidio) => MagnitudesPrincipal | null;
  /** Fecha de la revisión nueva, ya escrita para la persona («13 de octubre de 2026»). */
  fechaRevision: string;
}

const round = (n: number, dec: number): number => {
  const f = 10 ** dec;
  return Math.round(n * f) / f;
};
const fmt = (n: number, dec = 3): string => n.toLocaleString('es-MX', { maximumFractionDigits: dec });

/**
 * Los importes con la cantidad nueva: la MISMA cuenta que `fijarCantidad` (planoRenglonService), columna por
 * columna. `importe` solo cambia con precio unitario > 0; `apu_ia_importe`, solo si hay costo directo. Vista previa:
 * el valor que manda es el que devuelve el servidor.
 */
export function importeConCantidad(
  r: Pick<RenglonParaImpacto, 'precioUnitario' | 'importe' | 'apuIaCostoDirecto' | 'apuIaImporte'>,
  cantidad: number,
): { importe: number | null; apuIaImporte: number | null } {
  return {
    importe: r.precioUnitario != null && r.precioUnitario > 0 ? round(r.precioUnitario * cantidad, 2) : r.importe,
    apuIaImporte: r.apuIaCostoDirecto != null ? round(r.apuIaCostoDirecto * cantidad, 2) : r.apuIaImporte ?? null,
  };
}

/** Lo que el renglón suma a la hoja (vista previa): el importe de catálogo o, si no, el de su análisis con IA. */
const importeDeHoja = (importe: number | null, apuIa: number | null): number => importe ?? apuIa ?? 0;

const sinIds = (e: UnElemento): UnElemento => ({ tipo: e.tipo, clave: e.clave, ...(e.vista ? { vista: e.vista } : {}) });
const elementoDeFoto = (e: UnElemento): ElementoDelGenerador => ({ tipo: e.tipo, clave: e.clave, ...(e.vista ? { vista: e.vista } : {}) });

/** La foto de lo que ya no está: una línea en 0 por elemento, que cuadra con total 0. */
function fotoYaNoEsta(base: FotoGenerador, elementos: readonly UnElemento[], fecha: string): FotoGenerador {
  return {
    version: 2,
    origen: base.origen,
    magnitud: base.magnitud,
    unidad: base.unidad,
    decimales: base.decimales,
    total: 0,
    lineas: elementos.map((e) => ({
      elemento: elementoDeFoto(e),
      grupo: '',
      localizacion: `Ya no está en la revisión del ${fecha}`,
      piezas: null,
      largo: null,
      ancho: null,
      alto: null,
      parcial: 0,
      clase: base.origen === 'complementario' ? 'regla' : 'medida',
    })),
    ...(base.inputExtra ? { inputExtra: base.inputExtra } : {}),
    ...(base.regla ? { regla: base.regla } : {}),
  };
}

interface MapeoDeElemento {
  elA: UnElemento;
  mapeo: SeleccionMapeada;
}

/** Lo que se sabe de un principal ya calculado (para sus complementarios). `como` null = no se pudo medir. */
interface PrincipalCalculado {
  impacto: ImpactoDeRenglon;
  como: ComoSeMidio | null;
  elementos: Map<string, MapeoDeElemento>;
}

const NOMBRE_BASE: Record<DerivacionDeComplementario['derivaDe'], string> = {
  conteo: 'Piezas',
  largo: 'Largo',
  area: 'Área',
  volumen: 'Volumen',
  vanos: 'Puertas y ventanas',
};

/** Aplica el contrato de un complementario: la MISMA cuenta que el armado (incluida la medida del concepto). */
function aplicarDerivacion(c: DerivacionDeComplementario, mags: MagnitudesPrincipal): number | null {
  const d = derivarCantidadComplementaria(c, mags).cantidad;
  if (d == null) return null;
  return c.medidaM != null ? porMedidaDelConcepto(d, c.medidaM) : d;
}

function detalleDeDerivacion(c: DerivacionDeComplementario, mags: MagnitudesPrincipal): string {
  const b = derivarCantidadComplementaria({ derivaDe: c.derivaDe, factor: 1, incluyeVanos: c.incluyeVanos }, mags);
  return (
    `${NOMBRE_BASE[c.derivaDe]}${b.usaLargoConVanos ? ' (con los huecos)' : ''} ${b.cantidad != null ? fmt(b.cantidad) : '—'} × ${fmt(c.factor)}` +
    (c.medidaM != null ? ` × ${fmt(c.medidaM)} m (la medida que dice el concepto)` : '')
  );
}

/**
 * Renglón por renglón, qué le hace la revisión nueva a la hoja. Reglas:
 *  1. Volver a medir A con los mismos ajustes debe dar lo guardado; si no, «revisar» sin tocar.
 *  2. Corregido a mano: la cantidad no se toca; la nueva va como propuesta. La selección sí se actualiza.
 *  3. Un renglón que junta varios elementos (`ademas`) se mide elemento por elemento y se suma.
 *  4. Lo que ya no está queda en 0 («ya_no_esta»); el renglón nunca se borra.
 *  5. Lo que no sale de piezas medidas (supuestos, cantidades a mano, sin selección o sin foto) no se recalcula.
 *  6. El importe nuevo, con la misma cuenta de la hoja (vista previa).
 *  7. Un complementario se recalcula con su contrato (sin IA); sin contrato, o si su principal no se pudo medir,
 *     «revisar» sin propuesta. Si su cantidad descansaba en un supuesto, «revisar» con propuesta.
 */
export function impactoEnLaHoja(e: EntradaImpacto): ImpactoDeRenglon[] {
  const out = new Map<string, ImpactoDeRenglon>();
  const principales = new Map<string, PrincipalCalculado>();

  const base = (r: RenglonParaImpacto, estado: EstadoImpacto, motivo: string | null): ImpactoDeRenglon => ({
    id: r.id,
    estado,
    motivo,
    aMano: esAMano(r),
    cantidadSeAplicaSola: false,
    seleccionSeAplicaSola: false,
    cantidadAntes: r.cantidad,
    cantidadNueva: null,
    delta: null,
    importeAntes: r.importe,
    importeNuevo: null,
    apuIaImporteAntes: r.apuIaImporte ?? null,
    apuIaImporteNuevo: null,
    seleccionNueva: null,
    generadorNuevo: null,
    piezas: { ...CERO },
  });

  /** Arma el resultado con una foto nueva (o la de «ya no está») y decide el estado. */
  const conFotoNueva = (
    r: RenglonParaImpacto,
    foto: FotoGenerador,
    nueva: FotoGenerador,
    seleccionNueva: PlanoSeleccion | null,
    duda: { dudoso: boolean; revisar: boolean; motivos: string[]; todoSeFue: boolean; piezas: ConteoDePiezas },
  ): ImpactoDeRenglon => {
    const aMano = esAMano(r);
    const tol = toleranciaDeCuadre(Math.max(foto.decimales, nueva.decimales), 1);
    const igual = Math.abs(nueva.total - foto.total) <= tol;
    const motivos = [...duda.motivos];
    // Un renglón que junta elementos y uno PARCIAL se quedó sin piezas: su selección no se puede escribir (sin `ids`
    // sería el tipo entero; con `ids: []` la pantalla lo mide como el tipo entero). Si lo demás sigue, aplicar la
    // cantidad dejaría la selección vieja (ids y vista de A) midiendo otra cosa: lo decide la persona.
    const sinSeleccionEscribible = seleccionNueva == null && r.seleccion != null && !duda.todoSeFue;
    if (sinSeleccionEscribible) motivos.push('Una parte de este renglón ya no tiene piezas: revisa qué cubre.');
    let estado: EstadoImpacto;
    if (duda.dudoso || duda.revisar || sinSeleccionEscribible) estado = 'revisar';
    // Corregido a mano y ya no está: lo decide la persona (T2 lo sabe por `cantidadSeAplicaSola`).
    else if (duda.todoSeFue) estado = aMano ? 'revisar' : 'ya_no_esta';
    else if (igual) estado = 'igual';
    else if (aMano) estado = 'revisar';
    else estado = 'cambia';
    if (duda.todoSeFue && !duda.dudoso) motivos.unshift(`Ya no está en la revisión del ${e.fechaRevision}.`);
    if (aMano && !igual) motivos.push(`Corregiste la cantidad a mano: con la revisión nueva el plano mide ${fmt(nueva.total, nueva.decimales)}.`);
    else if (!igual && !duda.todoSeFue) motivos.push(`Pasa de ${fmt(foto.total, foto.decimales)} a ${fmt(nueva.total, nueva.decimales)}.`);
    const cantidadNueva = nueva.total;
    const imp = importeConCantidad(r, cantidadNueva);
    return {
      ...base(r, estado, motivos.length ? motivos.join(' ') : null),
      cantidadSeAplicaSola: !aMano && (estado === 'cambia' || estado === 'ya_no_esta'),
      seleccionSeAplicaSola: seleccionNueva != null && !duda.dudoso,
      cantidadNueva,
      delta: round(cantidadNueva - r.cantidad, 6),
      importeNuevo: imp.importe,
      apuIaImporteNuevo: imp.apuIaImporte,
      seleccionNueva,
      generadorNuevo: nueva,
      piezas: duda.piezas,
    };
  };

  const sumar = (a: ConteoDePiezas, b: ConteoDePiezas): ConteoDePiezas => ({
    agregadas: a.agregadas + b.agregadas,
    quitadas: a.quitadas + b.quitadas,
    cambiaron: a.cambiaron + b.cambiaron,
    cambioDeTipo: a.cambioDeTipo + b.cambioDeTipo,
    dudosas: a.dudosas + b.dudosas,
  });

  /**
   * La selección nueva de un renglón: lo mapeado y, lo que ya no está del tipo entero, sin piezas (sigue siendo su
   * identidad). Si un elemento PARCIAL se quedó sin piezas, no hay forma de escribirlo: null (no se reescribe).
   */
  const seleccionNuevaDe = (ms: readonly MapeoDeElemento[]): PlanoSeleccion | null =>
    ms.some((m) => !m.mapeo.seleccion && m.mapeo.parcialVacia) ? null : seleccionDeElementos(ms.map((m) => m.mapeo.seleccion ?? sinIds(m.elA)));

  // --- Principales (y todo lo que no es complementario) ---
  for (const r of e.renglones) {
    const foto = r.generador ?? null;
    if (foto?.origen === 'complementario') continue;
    if (!foto) {
      out.set(r.id, base(r, 'no_se_recalcula', 'Este renglón no guarda cómo se midió: revísalo a mano.'));
      continue;
    }
    if (foto.origen === 'no_dibujado') {
      out.set(r.id, base(r, 'no_se_recalcula', 'No sale de piezas dibujadas: no cambia con el plano.'));
      continue;
    }
    if (foto.origen === 'a_mano') {
      out.set(r.id, base(r, 'no_se_recalcula', 'La cantidad la escribiste tú: no cambia con el plano.'));
      continue;
    }
    if (!r.seleccion) {
      out.set(r.id, base(r, 'no_se_recalcula', 'No está ligado a ninguna pieza del plano.'));
      continue;
    }
    const como = comoSeMidioDeLaFoto(foto, { magnitud: r.magnitudConcepto, unidad: r.unidad });
    if (!como) {
      out.set(r.id, base(r, 'revisar', 'Este renglón se agregó antes de que se guardara cómo se midió: revísalo a mano.'));
      principales.set(r.id, { impacto: out.get(r.id)!, como: null, elementos: new Map() });
      continue;
    }
    const elementos = elementosDeSeleccion(r.seleccion);
    // 1. Autocomprobación: medir A con los mismos ajustes da lo guardado.
    const fotoA = juntarTodas(elementos.map((x) => e.medirA(x, como)));
    if (!fotoA || Math.abs(fotoA.total - foto.total) > toleranciaDeCuadre(foto.decimales, elementos.length)) {
      out.set(r.id, base(r, 'revisar', 'Tu hoja se midió con otros ajustes: revisa este renglón a mano.'));
      principales.set(r.id, { impacto: out.get(r.id)!, como: null, elementos: new Map() });
      continue;
    }
    const ms: MapeoDeElemento[] = elementos.map((elA) => ({ elA, mapeo: e.mapear(elA) }));
    const porClave = new Map(ms.map((m) => [claveDeElemento(m.elA), m] as const));
    const dudoso = ms.some((m) => m.mapeo.dudoso);
    const revisar = ms.some((m) => m.mapeo.revisar);
    const motivos = [...new Set(ms.map((m) => m.mapeo.motivo).filter((x): x is string => !!x && x !== YA_NO_ESTA))];
    const piezas = ms.reduce((s, m) => sumar(s, m.mapeo.piezas), { ...CERO });
    const quedan = ms.filter((m) => m.mapeo.seleccion);
    const seFueron = ms.filter((m) => !m.mapeo.seleccion);
    const seleccionNueva = seleccionNuevaDe(ms);
    const fotosB = quedan.map((m) => e.medirB(m.mapeo.seleccion!, como));
    if (fotosB.some((f) => !f)) {
      const imp: ImpactoDeRenglon = {
        ...base(r, 'revisar', [...motivos, 'Con la revisión nueva el plano no da la cantidad de este renglón: escríbela.'].join(' ')),
        seleccionNueva,
        seleccionSeAplicaSola: seleccionNueva != null && !dudoso,
        piezas,
      };
      out.set(r.id, imp);
      principales.set(r.id, { impacto: imp, como, elementos: porClave });
      continue;
    }
    const partes: FotoGenerador[] = [...(fotosB as FotoGenerador[])];
    if (seFueron.length > 0) {
      partes.push(fotoYaNoEsta(foto, seFueron.map((m) => m.elA), e.fechaRevision));
      if (quedan.length > 0) {
        motivos.push(`Ya no está${seFueron.length === 1 ? '' : 'n'} en la revisión del ${e.fechaRevision}: ${seFueron.map((m) => `«${m.elA.clave}»`).join(', ')}.`);
      }
    }
    const nueva = juntarTodas(partes)!;
    const imp = conFotoNueva(r, foto, nueva, seleccionNueva, {
      dudoso,
      revisar,
      motivos,
      todoSeFue: quedan.length === 0,
      piezas,
    });
    out.set(r.id, imp);
    principales.set(r.id, { impacto: imp, como, elementos: porClave });
  }

  // --- Complementarios: salen de sus principales con su contrato ---
  for (const r of e.renglones) {
    const foto = r.generador ?? null;
    if (foto?.origen !== 'complementario') continue;
    const padres = [r.principalId, ...(r.padres ?? [])].filter((x): x is string => !!x).map((id) => principales.get(id)).filter((x): x is PrincipalCalculado => !!x);
    /** El elemento en sus principales YA medidos; null = su principal no se pudo medir (no se adivina). */
    const buscar = (el: ElementoDelGenerador): { m: MapeoDeElemento; como: ComoSeMidio } | null => {
      const k = claveDeElemento(el);
      for (const p of padres) {
        const m = p.elementos.get(k);
        if (m && p.como) return { m, como: p.como };
      }
      return null;
    };
    const seleccionNueva = (() => {
      if (!r.seleccion) return null;
      const ms: MapeoDeElemento[] = [];
      for (const x of elementosDeSeleccion(r.seleccion)) {
        const b = buscar(elementoDeFoto(x));
        if (!b) return null;
        ms.push(b.m);
      }
      return seleccionNuevaDe(ms);
    })();
    const lineas = foto.lineas;
    const sinContrato = lineas.length === 0 || lineas.some((l) => !l.derivacion || !l.elemento);
    if (sinContrato) {
      const imps = padres.map((p) => p.impacto);
      if (imps.length > 0 && imps.every((i) => i.estado === 'ya_no_esta')) {
        const els = lineas.flatMap((l) => (l.elemento ? [l.elemento as UnElemento] : []));
        const nueva = fotoYaNoEsta(foto, els.length ? els : elementosDeSeleccion(r.seleccion), e.fechaRevision);
        out.set(r.id, conFotoNueva(r, foto, nueva, seleccionNueva, { dudoso: false, revisar: false, motivos: [], todoSeFue: true, piezas: { ...CERO } }));
      } else if (imps.length > 0 && imps.every((i) => i.estado === 'igual')) {
        out.set(r.id, {
          ...base(r, 'igual', null),
          cantidadNueva: foto.total,
          delta: round(foto.total - r.cantidad, 6),
          importeNuevo: r.importe,
          apuIaImporteNuevo: r.apuIaImporte ?? null,
        });
      } else {
        const cambio = padres.find((p) => p.impacto.estado !== 'igual');
        const loc = lineas[0]?.localizacion ?? 'su principal';
        const motivo =
          cambio && cambio.impacto.cantidadNueva != null
            ? `Sale de ${loc}, que cambió de ${fmt(cambio.impacto.cantidadAntes)} a ${fmt(cambio.impacto.cantidadNueva)}: revisa su cantidad a mano.`
            : `Sale de ${loc}, que cambió con la revisión nueva: revisa su cantidad a mano.`;
        out.set(r.id, base(r, 'revisar', motivo));
      }
      continue;
    }
    // Con contrato: cada línea se recalcula con las magnitudes de su elemento en B.
    const tol = toleranciaDeCuadre(3, 1);
    const nuevas: LineaDeRegla[] = [];
    let gone = 0;
    let piezas: ConteoDePiezas = { ...CERO };
    let dudoso = false;
    let revisarMapeo = false;
    let supuesto = false;
    let falla: string | null = null;
    const motivos: string[] = [];
    for (const l of lineas) {
      const c = l.derivacion!;
      const hallado = buscar(l.elemento!);
      if (!hallado) {
        falla = `No se pudo volver a medir ${l.localizacion}, de donde sale: revisa su cantidad a mano.`;
        break;
      }
      const { m, como } = hallado;
      piezas = sumar(piezas, m.mapeo.piezas);
      const dA = aplicarDerivacion(c, e.magnitudesA(m.elA, como) ?? {});
      if (dA == null || Math.abs(dA - l.parcial) > tol) {
        falla = 'Tu hoja se midió con otros ajustes: revisa este renglón a mano.';
        break;
      }
      if (m.mapeo.dudoso) dudoso = true;
      if (m.mapeo.revisar) revisarMapeo = true;
      if (c.supuesto) supuesto = true;
      if (m.mapeo.motivo && m.mapeo.motivo !== YA_NO_ESTA) motivos.push(m.mapeo.motivo);
      if (!m.mapeo.seleccion) {
        gone++;
        nuevas.push({ elemento: l.elemento, localizacion: l.localizacion, grupo: l.grupo, detalle: `Ya no está en la revisión del ${e.fechaRevision}`, parcial: 0, derivacion: c });
        continue;
      }
      const mags = e.magnitudesB(m.mapeo.seleccion, como) ?? {};
      const d = aplicarDerivacion(c, mags);
      if (d == null) {
        falla = `Con la revisión nueva no se puede medir de dónde sale (${l.localizacion}): escribe su cantidad.`;
        break;
      }
      nuevas.push({
        elemento: elementoDeFoto(m.mapeo.seleccion),
        localizacion: l.localizacion,
        grupo: l.grupo,
        detalle: detalleDeDerivacion(c, mags),
        parcial: d,
        derivacion: c,
      });
    }
    if (falla) {
      out.set(r.id, { ...base(r, 'revisar', [...new Set(motivos), falla].join(' ')), seleccionNueva, seleccionSeAplicaSola: seleccionNueva != null && !dudoso, piezas });
      continue;
    }
    const total = round(nuevas.reduce((s, x) => s + x.parcial, 0), 3);
    const nueva = fotoDeRegla({ origen: 'complementario', unidad: foto.unidad, total, lineas: nuevas, regla: foto.regla ?? null });
    const todoSeFue = gone === lineas.length;
    if (gone > 0 && !todoSeFue) motivos.push(`Una parte ya no está en la revisión del ${e.fechaRevision}.`);
    if (supuesto && !todoSeFue) motivos.push('Su cantidad usa una medida que el plano no trae: revísala.');
    // El supuesto deja de importar si ya no queda nada; lo que diga el mapeo (parcial vacía con su tipo vivo…), no.
    out.set(r.id, conFotoNueva(r, foto, nueva, seleccionNueva, { dudoso, revisar: revisarMapeo || (supuesto && !todoSeFue), motivos: [...new Set(motivos)], todoSeFue, piezas }));
  }

  return e.renglones.map((r) => out.get(r.id)!);
}

/** El renglón tiene una cantidad escrita a mano distinta de lo que midió el plano. */
function esAMano(r: RenglonParaImpacto): boolean {
  if (r.cantidadMedida == null) return false;
  const dec = r.generador?.decimales ?? 6;
  return Math.abs(r.cantidad - r.cantidadMedida) > toleranciaDeCuadre(dec, 1);
}

// ---------------------------------------------------------------------------------------------------
// Todo junto
// ---------------------------------------------------------------------------------------------------

export interface NuevoSinConcepto {
  grupo: string;
  clase: string;
  /** 'nueva' = no estaba en A; 'cambio_de_tipo' = estaba, pero era de otro tipo y ningún renglón cubre el de ahora. */
  origen: 'nueva' | 'cambio_de_tipo';
  piezas: PiezaComparable[];
}

export interface ComparacionDePlanos {
  version: 1;
  formato: 'ifc' | 'dwg';
  parametros: ParametrosCambioDePlano;
  piezas: CambioDePieza[];
  renglones: ImpactoDeRenglon[];
  /** Piezas de B que ningún renglón cubre (nuevas o de otro tipo): se listan, no se agregan solas. */
  nuevosSinConcepto: NuevoSinConcepto[];
  /** DWG: cómo se emparejaron las plantas. */
  vistas?: ResultadoVistas;
  /** Σ de lo que la hoja suma hoy (importe de catálogo o, si no, el del análisis con IA). Vista previa. */
  totalAntes: number;
  /** Σ con lo que se aplica solo (lo que queda para revisar cuenta con su importe de hoy). Vista previa. */
  totalNuevo: number;
  identidadesRegeneradas: boolean;
  /**
   * Cuánto se parecen las dos revisiones: piezas de cada lado y cuántas quedaron SIN pareja. Sirve para avisar «esto parece otro
   * plano» (`pareceOtroPlano`). Ausente en comparaciones guardadas antes de este campo.
   */
  parecido?: { piezasA: number; piezasB: number; sinParejaA: number; sinParejaB: number };
}

export interface EntradaComparacion extends Omit<EntradaImpacto, 'mapear'> {
  formato: 'ifc' | 'dwg';
  parametros: ParametrosCambioDePlano;
  piezasA: readonly PiezaComparable[];
  piezasB: readonly PiezaComparable[];
  /** DWG: las vistas (plantas) de cada revisión. */
  vistasA?: readonly VistaComparable[];
  vistasB?: readonly VistaComparable[];
}

/** Compara dos revisiones de un plano y su impacto en la hoja. */
export function compararRevisiones(e: EntradaComparacion): ComparacionDePlanos {
  const emparejado = emparejarPiezas(e.piezasA, e.piezasB, e.parametros, e.formato);
  const piezas = cambiosDePiezas(e.piezasA, e.piezasB, emparejado, e.parametros);
  const vistas = e.formato === 'dwg' && e.vistasA && e.vistasB ? emparejarVistas(e.vistasA, e.vistasB, e.parametros) : undefined;
  const mapear = crearMapeador({ formato: e.formato, piezasA: e.piezasA, piezasB: e.piezasB, emparejado, cambios: piezas, vistas });
  const renglones = impactoEnLaHoja({ ...e, mapear });

  // Lo que cubre la hoja en B: la selección nueva de cada renglón (o la de hoy, si no se recalculó).
  const cubiertas = new Set<string>();
  const tiposCubiertos = new Set<string>();
  const claveTipo = (tipo: string, grupo: string) => (e.formato === 'dwg' ? `${tipo}\u0000${grupo}` : grupo);
  e.renglones.forEach((r, i) => {
    const nueva = renglones[i]!.seleccionNueva;
    // Sin selección nueva: la de hoy cuenta solo si cubre el tipo entero (sus ids son de la revisión A).
    for (const x of elementosDeSeleccion(nueva ?? r.seleccion)) {
      if (x.ids) {
        if (nueva) x.ids.forEach((id) => cubiertas.add(id));
      } else if (x.vista) {
        // DWG: un renglón de UNA planta no cubre las demás (una planta nueva con la misma capa sigue sin concepto). Su vista
        // es la de B solo si se mapeó; la de hoy (id de A, posicional) no sirve para decidir qué cubre en B.
        if (nueva) tiposCubiertos.add(`${claveTipo(x.tipo, x.clave)}\u0000@${x.vista}`);
      } else tiposCubiertos.add(claveTipo(x.tipo, x.clave));
    }
  });
  const porGrupo = new Map<string, NuevoSinConcepto>();
  const porIdA = new Map(e.piezasA.map((p) => [p.id, p] as const));
  const porIdB = new Map(e.piezasB.map((p) => [p.id, p] as const));
  const sinCubrir = (p: PiezaComparable) =>
    !cubiertas.has(p.id) &&
    !tiposCubiertos.has(claveTipo(p.clase, p.grupo)) &&
    !(p.nivel != null && tiposCubiertos.has(`${claveTipo(p.clase, p.grupo)}\u0000@${p.nivel}`));
  const listar = (p: PiezaComparable, origen: NuevoSinConcepto['origen']) => {
    const k = `${origen}\u0000${claveTipo(p.clase, p.grupo)}`;
    (porGrupo.get(k) ?? porGrupo.set(k, { grupo: p.grupo, clase: p.clase, origen, piezas: [] }).get(k)!).piezas.push(p);
  };
  for (const id of emparejado.soloB) {
    const p = porIdB.get(id);
    if (p && sinCubrir(p)) listar(p, 'nueva');
  }
  for (const par of emparejado.parejas) {
    const pa = porIdA.get(par.a);
    const pb = porIdB.get(par.b);
    if (pa && pb && (pa.grupo !== pb.grupo || pa.clase !== pb.clase) && sinCubrir(pb)) listar(pb, 'cambio_de_tipo');
  }

  const totalAntes = round(renglones.reduce((s, x) => s + importeDeHoja(x.importeAntes, x.apuIaImporteAntes), 0), 2);
  const totalNuevo = round(
    renglones.reduce(
      (s, x) => s + (x.cantidadSeAplicaSola ? importeDeHoja(x.importeNuevo, x.apuIaImporteNuevo) : importeDeHoja(x.importeAntes, x.apuIaImporteAntes)),
      0,
    ),
    2,
  );
  return {
    version: 1,
    formato: e.formato,
    parametros: e.parametros,
    piezas,
    renglones,
    nuevosSinConcepto: [...porGrupo.values()],
    ...(vistas ? { vistas } : {}),
    totalAntes,
    totalNuevo,
    identidadesRegeneradas: emparejado.identidadesRegeneradas,
    parecido: { piezasA: e.piezasA.length, piezasB: e.piezasB.length, sinParejaA: emparejado.soloA.length, sinParejaB: emparejado.soloB.length },
  };
}
